import React, { useState } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import getViewInCalendarMonth from "../../utils/get-view-month";
import ToolTip from "../tooltip";
import { STRIPE } from "../../constants";

// load utils
import getCycleCardImagePath from "../../utils/get-cycle-card-image-path";
import SvgIcon from "../SvgIcon";

const ProgramCard = (props, { locals }) => {
  // cycle object is not available in program-builder "programs" view
  const {
    program,
    cycle,
    cardIndex,
    referrer,
    activeProgramIndex,
    athlete,
    isTemplate,
  } = props;

  // Don't include the active program in "More programs" on the overview page
  if (props.isOverview && program.order - 1 === activeProgramIndex) return null;

  // const orderInCycle = program.orderInCycle || cycle.programs.indexOf(program) + 1;
  const programTotal = program.cycleProgramTotal || cycle.programs.length;

  const cycleId = program.cycleId || cycle._id;
  const isPremium = cycle
    ? cycle.type === "premium"
    : program.cycleType === "premium";
  const isUser = cycle ? cycle.type === "user" : program.cycleType === "user";
  const isCoach = cycle
    ? cycle.type === "coach"
    : program.cycleType === "coach";
  const isPushed = cycle
    ? cycle.status === "pushed"
    : program.cycleType === "pushed";

  const cycleAuthor =
    get(cycle, "createdBy.name", false) || program.cycleAuthor;
  const [firstName, lastName] = cycleAuthor.split(/ +/);
  const cycleImage = getCycleCardImagePath(cycle);

  const { activeUser, activeUserToRequest } = locals;
  const inCoachMode =
    isCoach &&
    String(cycle ? cycle.createdBy._id : program.cycleAuthorId) ===
      String(activeUser._id);
  const viewInCalendarMonth = getViewInCalendarMonth(
    program.progress,
    program.instances
  );
  const viewInCalendarURL =
    inCoachMode && athlete
      ? `/athlete-portal/calendar?${
          viewInCalendarMonth ? `month=${viewInCalendarMonth}` : ""
        }&athlete=${athlete._id}`
      : `/dashboard/calendar/?${
          viewInCalendarMonth ? `month=${viewInCalendarMonth}` : ""
        }`;

  const progressStyles = {
    "In Progress": "inprogress",
    Upcoming: "upcoming",
    Completed: "completed",
  };

  const progress = progressStyles[program.progress];
  const userPlan = get(activeUserToRequest, "stripe.plan", "");
  const isUserPremium =
    userPlan === STRIPE.PLANS.V2.premiumMonthly ||
    userPlan === STRIPE.PLANS.V2.premiumAnnually;

  const isAdmin = activeUser.permissions.includes("admin");
  const suggestUpradePath = "/actions/refresh?modal[type]=suggest-upgrade";
  const addToCalendarPath = `/actions/refresh?modal[type]=add-to-calendar&cycle=${cycleId}&program=${program.order}&wholeCycle=false`;

  const fromProgramLibrary = cycle
    ? cycle.status === "published"
    : program.status === "published" || program.cycleStatus === "published";
  const parentPath = `${
    athlete
      ? "/athlete-portal/programs"
      : isTemplate
      ? "/coaching-portal/templates"
      : "/dashboard/program-builder"
  }`;

  return (
    <article className="column" data-col="1/3">
      <div
        className={`Cycle-Builder-Preview ${isPremium ? "alt-premium" : ""}`}
      >
        <div
          className="image relative"
          style={{
            backgroundImage: `url(${cycle ? cycleImage : program.cycleImage})`,
          }}
        >
          <a
            href={`${parentPath}/program-overview/${cycleId}?program=${program.order}&referrer=${referrer}&athlete=${athlete}`}
          >
            Overview
          </a>
        </div>
        <div className="body">
          <h2 className={`title ${progress || "notscheduled"}`}>
            {program.name}
          </h2>
          <div className="number">
            <b>{program.order}</b>/{programTotal}
          </div>
          {athlete && (
            <ToolTip id="push" body="Push">
              <a
                href={`/actions/refresh?modal[type]=confirm-push&cycle=${cycleId}&program=${program.order}`}
              >
                <div className="add">+</div>
              </a>
            </ToolTip>
          )}
        </div>
      </div>
      <footer>
        <div className="title">{program.cycleName || cycle.name}</div>

        <Dropdown
          inCoachMode={inCoachMode}
          cycleId={cycleId}
          program={program}
          isPushed={isPushed}
          viewInCalendarURL={viewInCalendarURL}
          fromProgramLibrary={fromProgramLibrary}
          parentPath={parentPath}
          isTemplate={isTemplate}
        />
      </footer>
    </article>
  );
};

ProgramCard.contextTypes = { locals: PropTypes.object };

export default ProgramCard;

const Dropdown = ({
  inCoachMode,
  cycleId,
  program,
  isPushed,
  viewInCalendarURL,
  fromProgramLibrary,
  parentPath,
  isTemplate,
}) => {
  const [isOpened, setIsOpened] = useState(false);
  return (
    <details role="list" open className="absolute top-1 right-1">
      <summary
        onClick={(e) => {
          e.preventDefault();
          setIsOpened(!isOpened);
        }}
      >
        <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
      </summary>
      <ul role="listbox" className={isOpened ? "opened" : ""}>
        {!inCoachMode && (
          <li>
            <a
              href={`/actions/refresh?modal[type]=add-to-calendar&cycle=${cycleId}&program=${program.order}&wholeCycle=false`}
            >
              <i className="icon">
                <img src="/img/icons/plus_white.svg" alt="" />
              </i>
              Add to Calendar
            </a>
          </li>
        )}
        {!isTemplate && inCoachMode && !isPushed && (
          <li>
            <a
              href={`/actions/refresh?modal[type]=confirm-push&cycle=${cycleId}&program=${program.order}`}
            >
              <i className="icon">
                <img src="/img/icons/plus_white.svg" alt="" />
              </i>
              Push
            </a>
          </li>
        )}
        {inCoachMode && (
          <li>
            <a
              href={`/actions/refresh?modal[type]=send&cycleId=${cycleId}&programOrder=${program.order}`}
            >
              <i className="icon">
                <img src="/img/icons/copy_white.svg" alt="" />
              </i>
              Send Program
            </a>
          </li>
        )}

        {!isTemplate && program.progress !== "Unscheduled" && (
          <li>
            <a href={viewInCalendarURL}>
              <i className="icon">
                <img src="/img/icons/calendar_white.svg" alt="" />
              </i>
              View in Calendar
            </a>
          </li>
        )}
        {!fromProgramLibrary && (
          <li>
            <a
              href={`/actions/cycle/program/duplicate/${cycleId}?program=${program.order}`}
            >
              <i className="icon">
                <img src="/img/icons/duplicate_white.svg" alt="" />
              </i>
              Duplicate
            </a>
          </li>
        )}
        {!fromProgramLibrary && !isPushed && (
          <li>
            <a
              href={`/actions/refresh?modal[type]=edit-program-settings&cycleId=${cycleId}&program=${program.order}`}
            >
              <i className="icon">
                <img src="/img/icons/settings_white.svg" alt="" />
              </i>
              Settings
            </a>
          </li>
        )}
        {!fromProgramLibrary && !isPushed && (
          <li>
            <a
              href={`${parentPath}/edit-program/${cycleId}?program=${program.order}&athlete=${athlete}`}
            >
              <i className="icon">
                <img src="/img/icons/edit_white.svg" alt="" />
              </i>
              Edit
            </a>
          </li>
        )}
        <li>
          <a
            href={`/actions/cycle/delete-program/${cycleId}/${program.order}/?isFromCoachingPortal=${inCoachMode}`}
          >
            <i className="icon">
              <img src="/img/icons/remove_white.svg" alt="remove" />
            </i>
            Delete
          </a>
        </li>
      </ul>
    </details>
  );
};
